import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocalParticipant, useParticipants } from '@livekit/components-react';
import { Track } from 'livekit-client';
import type { Participant } from 'livekit-client';
import type { ActiveSpeakerTuning } from '@modelcast/types';
import { useRoom } from '../room/RoomProvider';
import { getPreferredCameraDeviceId } from './cameraDevicePreference';
import { sendVideoDiagnosticReport } from './videoDiagnostics';

type ParticipantRole = 'model' | 'organizer' | 'artist';

type DiagnosticTrackRow = {
  participantId: string;
  participantName: string;
  role: ParticipantRole;
  direction: 'outbound' | 'inbound';
  trackSid: string | null;
  subscribed: boolean | null;
  muted: boolean | null;
  width: number | null;
  height: number | null;
  framesPerSecond: number | null;
  bitrateMbps: number | null;
  packetsLost: number | null;
  qualityLimitationReason: string | null;
  videoQuality: string | null;
  status: 'ok' | 'warn';
  warning: string | null;
};

type DiagnosticCameraRow = {
  participantId: string;
  participantName: string;
  sessionRole: 'host' | 'model' | 'artist';
  profileId: string | null;
  isLiveKitPresent: boolean;
  isLocal: boolean;
  isActiveModelProfile: boolean;
  isActiveModelCamera: boolean;
  cameraLabel: string | null;
  hasCameraPublication: boolean;
  cameraMuted: boolean | null;
  cameraSubscribed: boolean | null;
  trackSid: string | null;
  trackName: string | null;
  videoQuality: string | null;
  mediaDeviceId: string | null;
  mediaDeviceMatchesPreferred: boolean;
  mediaLabel: string | null;
  mediaWidth: number | null;
  mediaHeight: number | null;
  mediaFrameRate: number | null;
};

type DiagnosticCameraSummary = {
  activeModelProfileId: string | null;
  representativeModelParticipantId: string | null;
  activeModelParticipantIds: string[];
  preferredCameraDeviceId: string | null;
  cameraLabels: Record<string, string>;
  activeCameraCount: number;
  switcherCameraCount: number;
  duplicateLabels: string[];
  unlabeledActiveCameraParticipantIds: string[];
  rows: DiagnosticCameraRow[];
};

type DiagnosticsSnapshot = {
  capturedAt: string;
  roomCode: string;
  participantCount: number;
  activeSpeakerTuning: ActiveSpeakerTuning;
  modelProfileId: string | null;
  modelParticipantId: string | null;
  modelParticipantIds: string[];
  hostParticipantIds: string[];
  cameraSummary: DiagnosticCameraSummary;
  rows: DiagnosticTrackRow[];
  warnings: string[];
};

type StatsTrack = {
  currentBitrate?: number;
  dimensions?: { width?: number; height?: number };
  mediaStreamTrack?: MediaStreamTrack;
  getRTCStatsReport?: () => Promise<RTCStatsReport | undefined>;
};

type PreviousStatsSample = {
  at: number;
  bytes: number;
};

declare global {
  interface Window {
    __drawclubRoomDiagnosticsSnapshot?: DiagnosticsSnapshot;
    __drawclubGetRoomDiagnostics?: () => DiagnosticsSnapshot | null;
  }
}

const NON_MODEL_MAX_BITRATE_MBPS = 1.5;
const NON_MODEL_MAX_SHORT_EDGE = 720;

type TuningField = {
  key: keyof ActiveSpeakerTuning;
  label: string;
  description: string;
  min: number;
  max: number;
  step: number;
  unit?: string;
  digits?: number;
};

const ACTIVE_SPEAKER_TUNING_FIELDS: TuningField[] = [
  {
    key: 'minAudioLevel',
    label: 'Minimum audio level',
    description: 'Raise this to ignore room noise.',
    min: 0,
    max: 0.3,
    step: 0.01,
    digits: 2,
  },
  {
    key: 'switchDwellMs',
    label: 'New speaker dwell',
    description: 'How long a new speaker must stay active.',
    min: 0,
    max: 2500,
    step: 50,
    unit: 'ms',
  },
  {
    key: 'minDisplayMs',
    label: 'Minimum display time',
    description: 'Minimum time before the camera can switch again.',
    min: 0,
    max: 5000,
    step: 100,
    unit: 'ms',
  },
  {
    key: 'switchMargin',
    label: 'Switch margin',
    description: 'Extra loudness needed while the current speaker is still active.',
    min: 0,
    max: 0.2,
    step: 0.01,
    digits: 2,
  },
  {
    key: 'currentLevelHoldMs',
    label: 'Previous level hold',
    description: 'How quickly the old speaker level expires.',
    min: 0,
    max: 2000,
    step: 50,
    unit: 'ms',
  },
];

function hasDiagnosticsQueryParam() {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('roomDiagnostics') === 'true' || params.get('loadTest') === 'true';
}

function isTextEntryTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;

  const tagName = target.tagName.toLowerCase();
  return (
    tagName === 'input' ||
    tagName === 'textarea' ||
    tagName === 'select' ||
    target.isContentEditable
  );
}

function formatBitrate(value: number | null) {
  if (value === null || !Number.isFinite(value)) return '...';
  if (value < 0.1) return `${Math.round(value * 1000)} Kbps`;
  return `${value.toFixed(2)} Mbps`;
}

function formatSize(width: number | null, height: number | null) {
  if (!width || !height) return '...';
  return `${width} x ${height}`;
}

function formatTuningValue(field: TuningField, value: number) {
  const formatted = field.digits === undefined ? Math.round(value).toString() : value.toFixed(field.digits);
  return field.unit ? `${formatted} ${field.unit}` : formatted;
}

function getVideoQualityLabel(value: unknown) {
  if (value === 0) return 'low';
  if (value === 1) return 'medium';
  if (value === 2) return 'high';
  if (typeof value === 'string') return value;
  return null;
}

function getParticipantRole(
  participantId: string,
  modelParticipantIds: string[],
  hostParticipantIds: string[]
): ParticipantRole {
  if (modelParticipantIds.includes(participantId)) return 'model';
  if (hostParticipantIds.includes(participantId)) return 'organizer';
  return 'artist';
}

function getParticipantDisplayName(participant: Participant) {
  return participant.name || participant.identity.slice(0, 8);
}

function getShortId(value: string | null | undefined) {
  if (!value) return 'none';
  return value.length > 8 ? `${value.slice(0, 8)}...` : value;
}

function getOptionalShortId(value: string | null | undefined) {
  if (!value) return '...';
  return getShortId(value);
}

function getCameraPublication(participant: Participant | undefined) {
  return participant?.getTrackPublication(Track.Source.Camera) ?? null;
}

function getMediaSettingsForPublication(publication: ReturnType<typeof getCameraPublication>) {
  const mediaStreamTrack = (publication?.track as StatsTrack | undefined)?.mediaStreamTrack;
  const settings = mediaStreamTrack?.getSettings();

  return {
    deviceId: typeof settings?.deviceId === 'string' ? settings.deviceId : null,
    label: mediaStreamTrack?.label || null,
    width: typeof settings?.width === 'number' ? settings.width : null,
    height: typeof settings?.height === 'number' ? settings.height : null,
    frameRate: typeof settings?.frameRate === 'number' ? settings.frameRate : null,
  };
}

function buildCameraSummary({
  liveKitParticipants,
  sessionParticipants,
  modelProfileId,
  modelParticipantId,
  modelParticipantIds,
  modelCameraLabels,
}: {
  liveKitParticipants: Participant[];
  sessionParticipants: ReturnType<typeof useRoom>['participants'];
  modelProfileId: string | null;
  modelParticipantId: string | null;
  modelParticipantIds: string[];
  modelCameraLabels: Record<string, string>;
}): DiagnosticCameraSummary {
  const liveKitParticipantById = new Map(
    liveKitParticipants.map(participant => [participant.identity, participant])
  );
  const preferredCameraDeviceId = getPreferredCameraDeviceId();
  const sessionParticipantById = new Map(
    sessionParticipants.map(participant => [participant.participantId, participant])
  );
  const allParticipantIds = Array.from(new Set([
    ...sessionParticipants.map(participant => participant.participantId),
    ...liveKitParticipants.map(participant => participant.identity),
    ...modelParticipantIds,
  ]));

  const rows = allParticipantIds.map((participantId) => {
    const sessionParticipant = sessionParticipantById.get(participantId);
    const liveKitParticipant = liveKitParticipantById.get(participantId);
    const publication = getCameraPublication(liveKitParticipant);
    const mediaSettings = getMediaSettingsForPublication(publication);
    const profileId = sessionParticipant?.profileId ?? null;

    return {
      participantId,
      participantName: sessionParticipant?.displayName ||
        (liveKitParticipant ? getParticipantDisplayName(liveKitParticipant) : participantId.slice(0, 8)),
      sessionRole: sessionParticipant?.role ?? 'artist',
      profileId,
      isLiveKitPresent: Boolean(liveKitParticipant),
      isLocal: liveKitParticipant?.isLocal ?? false,
      isActiveModelProfile: Boolean(modelProfileId && profileId === modelProfileId),
      isActiveModelCamera: modelParticipantIds.includes(participantId),
      cameraLabel: modelCameraLabels[participantId] ?? null,
      hasCameraPublication: Boolean(publication),
      cameraMuted: typeof publication?.isMuted === 'boolean' ? publication.isMuted : null,
      cameraSubscribed: typeof publication?.isSubscribed === 'boolean' ? publication.isSubscribed : null,
      trackSid: publication?.trackSid || (publication as unknown as { sid?: string } | null)?.sid || null,
      trackName: publication?.trackName ?? null,
      videoQuality: getVideoQualityLabel((publication as unknown as { videoQuality?: unknown } | null)?.videoQuality),
      mediaDeviceId: mediaSettings.deviceId,
      mediaDeviceMatchesPreferred: Boolean(
        mediaSettings.deviceId &&
        preferredCameraDeviceId &&
        mediaSettings.deviceId === preferredCameraDeviceId
      ),
      mediaLabel: mediaSettings.label,
      mediaWidth: mediaSettings.width,
      mediaHeight: mediaSettings.height,
      mediaFrameRate: mediaSettings.frameRate,
    } satisfies DiagnosticCameraRow;
  });

  const activeCameraRows = rows.filter(row => row.isActiveModelCamera);
  const switcherCameraRows = activeCameraRows.filter(row => row.hasCameraPublication && row.cameraMuted !== true);
  const labelCounts = new Map<string, number>();

  for (const row of activeCameraRows) {
    if (!row.cameraLabel) continue;
    labelCounts.set(row.cameraLabel, (labelCounts.get(row.cameraLabel) ?? 0) + 1);
  }

  return {
    activeModelProfileId: modelProfileId,
    representativeModelParticipantId: modelParticipantId,
    activeModelParticipantIds: modelParticipantIds,
    preferredCameraDeviceId,
    cameraLabels: modelCameraLabels,
    activeCameraCount: activeCameraRows.length,
    switcherCameraCount: switcherCameraRows.length,
    duplicateLabels: Array.from(labelCounts.entries())
      .filter(([, count]) => count > 1)
      .map(([label]) => label),
    unlabeledActiveCameraParticipantIds: activeCameraRows
      .filter(row => !row.cameraLabel)
      .map(row => row.participantId),
    rows,
  };
}

function getDiagnosticStatus(row: Omit<DiagnosticTrackRow, 'status' | 'warning'>) {
  if (row.role === 'model') {
    return { status: 'ok' as const, warning: null };
  }

  const shortEdge = row.width && row.height ? Math.min(row.width, row.height) : null;
  if (row.bitrateMbps !== null && row.bitrateMbps > NON_MODEL_MAX_BITRATE_MBPS) {
    return {
      status: 'warn' as const,
      warning: `Non-model camera is publishing or receiving ${row.bitrateMbps.toFixed(2)} Mbps.`,
    };
  }

  if (shortEdge !== null && shortEdge > NON_MODEL_MAX_SHORT_EDGE) {
    return {
      status: 'warn' as const,
      warning: `Non-model camera frame is ${row.width} x ${row.height}.`,
    };
  }

  return { status: 'ok' as const, warning: null };
}

function getPrimaryVideoStat(report: RTCStatsReport | undefined, direction: 'outbound' | 'inbound') {
  if (!report) return null;

  const targetType = direction === 'outbound' ? 'outbound-rtp' : 'inbound-rtp';
  let selected: RTCStats | null = null;

  report.forEach((stat) => {
    const item = stat as RTCStats & Record<string, unknown>;
    if (item.type !== targetType) return;
    const kind = item.kind ?? item.mediaType;
    if (kind && kind !== 'video') return;

    const selectedBytes = direction === 'outbound'
      ? Number((selected as Record<string, unknown> | null)?.bytesSent ?? 0)
      : Number((selected as Record<string, unknown> | null)?.bytesReceived ?? 0);
    const itemBytes = direction === 'outbound'
      ? Number(item.bytesSent ?? 0)
      : Number(item.bytesReceived ?? 0);

    if (!selected || itemBytes >= selectedBytes) {
      selected = stat;
    }
  });

  return selected as (RTCStats & Record<string, unknown>) | null;
}

function getPacketLoss(report: RTCStatsReport | undefined) {
  if (!report) return null;

  let packetsLost: number | null = null;
  report.forEach((stat) => {
    const item = stat as RTCStats & Record<string, unknown>;
    if (!['inbound-rtp', 'remote-inbound-rtp'].includes(item.type)) return;
    if (typeof item.packetsLost !== 'number') return;
    packetsLost = Math.max(packetsLost ?? 0, item.packetsLost);
  });

  return packetsLost;
}

async function getTrackStatsRow({
  participant,
  role,
  previousSamples,
}: {
  participant: Participant;
  role: ParticipantRole;
  previousSamples: Map<string, PreviousStatsSample>;
}): Promise<DiagnosticTrackRow | null> {
  const publication = participant.getTrackPublication(Track.Source.Camera);
  if (!publication) return null;

  const track = publication.track as StatsTrack | undefined;
  const direction: DiagnosticTrackRow['direction'] = participant.isLocal ? 'outbound' : 'inbound';
  const trackSid = publication.trackSid || (publication as unknown as { sid?: string }).sid || null;
  const rowKey = `${participant.identity}:${direction}:${trackSid ?? 'camera'}`;
  const report = await track?.getRTCStatsReport?.();
  const stat = getPrimaryVideoStat(report, direction);
  const now = performance.now();
  const bytes = direction === 'outbound'
    ? Number(stat?.bytesSent ?? 0)
    : Number(stat?.bytesReceived ?? 0);
  const previous = previousSamples.get(rowKey);
  const elapsedSeconds = previous ? (now - previous.at) / 1000 : 0;
  const bitrateMbps = previous && elapsedSeconds > 0 && bytes >= previous.bytes
    ? ((bytes - previous.bytes) * 8) / elapsedSeconds / 1_000_000
    : typeof track?.currentBitrate === 'number'
      ? track.currentBitrate / 1_000_000
      : null;

  if (bytes > 0) {
    previousSamples.set(rowKey, { at: now, bytes });
  }

  const mediaSettings = track?.mediaStreamTrack?.getSettings();
  const width = typeof stat?.frameWidth === 'number'
    ? stat.frameWidth
    : track?.dimensions?.width ?? mediaSettings?.width ?? null;
  const height = typeof stat?.frameHeight === 'number'
    ? stat.frameHeight
    : track?.dimensions?.height ?? mediaSettings?.height ?? null;
  const baseRow = {
    participantId: participant.identity,
    participantName: getParticipantDisplayName(participant),
    role,
    direction,
    trackSid,
    subscribed: typeof publication.isSubscribed === 'boolean' ? publication.isSubscribed : null,
    muted: typeof publication.isMuted === 'boolean' ? publication.isMuted : null,
    width,
    height,
    framesPerSecond: typeof stat?.framesPerSecond === 'number' ? stat.framesPerSecond : mediaSettings?.frameRate ?? null,
    bitrateMbps,
    packetsLost: getPacketLoss(report),
    qualityLimitationReason: typeof stat?.qualityLimitationReason === 'string' ? stat.qualityLimitationReason : null,
    videoQuality: getVideoQualityLabel((publication as unknown as { videoQuality?: unknown }).videoQuality),
  };
  const diagnosticStatus = getDiagnosticStatus(baseRow);

  return {
    ...baseRow,
    ...diagnosticStatus,
  };
}

export function RoomDiagnosticsOverlay() {
  const liveKitParticipants = useParticipants();
  const { localParticipant } = useLocalParticipant();
  const {
    roomCode,
    hostParticipantIds,
    modelParticipantId,
    modelProfileId,
    modelParticipantIds,
    modelCameraLabels,
    activeSpeakerTuning,
    updateActiveSpeakerTuning,
    resetActiveSpeakerTuning,
    participants: sessionParticipants,
  } = useRoom();
  const queryEnabled = useMemo(() => hasDiagnosticsQueryParam(), []);
  const [isOpen, setIsOpen] = useState(false);
  const enabled = isOpen || queryEnabled;
  const [snapshot, setSnapshot] = useState<DiagnosticsSnapshot | null>(null);
  const [sendStatus, setSendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const previousSamplesRef = useRef(new Map<string, PreviousStatsSample>());

  useEffect(() => {
    if (!enabled) {
      delete window.__drawclubRoomDiagnosticsSnapshot;
      delete window.__drawclubGetRoomDiagnostics;
      return;
    }

    window.__drawclubGetRoomDiagnostics = () => window.__drawclubRoomDiagnosticsSnapshot ?? null;
    return () => {
      delete window.__drawclubRoomDiagnosticsSnapshot;
      delete window.__drawclubGetRoomDiagnostics;
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    const collect = async () => {
      const rows = await Promise.all(
        liveKitParticipants.map((participant) => getTrackStatsRow({
          participant,
          role: getParticipantRole(participant.identity, modelParticipantIds, hostParticipantIds),
          previousSamples: previousSamplesRef.current,
        }))
      );
      if (cancelled) return;

      const nextRows = rows.filter((row): row is DiagnosticTrackRow => Boolean(row));
      const warnings = nextRows
        .map((row) => row.warning)
        .filter((warning): warning is string => Boolean(warning));
      const cameraSummary = buildCameraSummary({
        liveKitParticipants,
        sessionParticipants,
        modelProfileId,
        modelParticipantId,
        modelParticipantIds,
        modelCameraLabels,
      });
      const nextSnapshot: DiagnosticsSnapshot = {
        capturedAt: new Date().toISOString(),
        roomCode,
        participantCount: liveKitParticipants.length,
        activeSpeakerTuning,
        modelProfileId,
        modelParticipantId,
        modelParticipantIds,
        hostParticipantIds,
        cameraSummary,
        rows: nextRows,
        warnings: [
          ...warnings,
          ...cameraSummary.duplicateLabels.map(label => `Duplicate active model camera label: ${label}.`),
          ...cameraSummary.unlabeledActiveCameraParticipantIds.map(
            participantId => `Active model camera has no central label: ${participantId}.`
          ),
        ],
      };

      window.__drawclubRoomDiagnosticsSnapshot = nextSnapshot;
      setSnapshot(nextSnapshot);
    };

    void collect();
    const interval = window.setInterval(() => void collect(), 2000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [
    enabled,
    activeSpeakerTuning,
    hostParticipantIds,
    liveKitParticipants,
    modelCameraLabels,
    modelParticipantId,
    modelParticipantIds,
    modelProfileId,
    roomCode,
    sessionParticipants,
  ]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat || isTextEntryTarget(event.target)) return;

      if (event.key === 'Escape') {
        setIsOpen(false);
        return;
      }

      if (event.key.toLowerCase() !== 'd' || !event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }

      event.preventDefault();
      setIsOpen((value) => !value);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const localRole = localParticipant?.identity
    ? getParticipantRole(localParticipant.identity, modelParticipantIds, hostParticipantIds)
    : null;
  const canTuneActiveSpeaker = localRole === 'organizer' || localRole === 'model';
  const handleSendToRich = async () => {
    if (!snapshot || sendStatus === 'sending') return;

    setSendStatus('sending');
    try {
      await sendVideoDiagnosticReport('room-diagnostics-manual-report', {
        sentBy: 'room-diagnostics-overlay',
        snapshot,
        localParticipant: {
          participantId: localParticipant?.identity ?? null,
          participantName: localParticipant?.name ?? null,
          role: localRole,
        },
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
          devicePixelRatio: window.devicePixelRatio,
        },
      }, {
        roomCode,
        participantId: localParticipant?.identity ?? null,
        participantName: localParticipant?.name ?? null,
        participantRole: localRole,
      });
      setSendStatus('sent');
    } catch (error) {
      console.warn('[room-diagnostics] Failed to send diagnostics report', error);
      setSendStatus('error');
    }
  };
  const sendButtonLabel = sendStatus === 'sending'
    ? 'Sending...'
    : sendStatus === 'sent'
      ? 'Sent'
      : sendStatus === 'error'
        ? 'Try again'
        : 'Send to Rich';

  useEffect(() => {
    if (sendStatus === 'idle' || sendStatus === 'sending') return;

    const timeout = window.setTimeout(() => setSendStatus('idle'), 3000);
    return () => window.clearTimeout(timeout);
  }, [sendStatus]);

  const nonModelRows = snapshot?.rows.filter((row) => row.role !== 'model') ?? [];
  const maxNonModelBitrate = nonModelRows.reduce((max, row) => (
    row.bitrateMbps === null ? max : Math.max(max, row.bitrateMbps)
  ), 0);
  const hasWarnings = Boolean(snapshot?.warnings.length);
  const cameraSummary = snapshot?.cameraSummary;
  const handleTuningChange = (key: keyof ActiveSpeakerTuning, value: number) => {
    if (!canTuneActiveSpeaker) return;
    updateActiveSpeakerTuning({ [key]: value } as Partial<ActiveSpeakerTuning>);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-[calc(env(safe-area-inset-bottom)+0.75rem)] left-[calc(env(safe-area-inset-left)+0.75rem)] z-[9999] max-w-[calc(100vw-1.5rem)]">
      <div className="mb-2 flex w-[min(720px,calc(100vw-1.5rem))] max-h-[72vh] flex-col overflow-hidden rounded-lg border border-white/16 bg-[#111] text-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-white/12 px-4 py-3">
          <div>
            <h2 className="font-display text-lg leading-tight">Room diagnostics</h2>
            <p className="mt-1 text-xs text-white/60">
              Local diagnostic view · role {localRole ?? 'unknown'} · Shift+D toggles · updates every 2 seconds
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSendToRich}
              disabled={!snapshot || sendStatus === 'sending'}
              className="rounded border border-white/20 px-2 py-1 text-xs text-white/70 hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-45"
            >
              {sendButtonLabel}
            </button>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="rounded border border-white/20 px-2 py-1 text-xs text-white/70 hover:bg-white/10 hover:text-white"
            >
              Close
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 border-b border-white/12 text-sm">
          <div className="px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-white/45">Participants</div>
            <div className="mt-1 font-mono text-xl">{snapshot?.participantCount ?? liveKitParticipants.length}</div>
          </div>
          <div className="px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-white/45">Non-model max</div>
            <div className={`mt-1 font-mono text-xl ${hasWarnings ? 'text-[#ffb199]' : 'text-white'}`}>
              {formatBitrate(maxNonModelBitrate)}
            </div>
          </div>
          <div className="px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-white/45">Status</div>
            <div className={`mt-1 font-mono text-xl ${hasWarnings ? 'text-[#ffb199]' : 'text-[#b8f0c2]'}`}>
              {hasWarnings ? 'Check' : 'OK'}
            </div>
          </div>
        </div>

        <div className="border-b border-white/12 px-4 py-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="font-display text-sm leading-tight">Active speaker tuning</h3>
              <p className="mt-1 text-[11px] text-white/50">
                Shared live tweaks for active speaker display.
              </p>
              {!canTuneActiveSpeaker && (
                <p className="mt-1 text-[10px] text-white/35">
                  Only session controls can change these values.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={resetActiveSpeakerTuning}
              disabled={!canTuneActiveSpeaker}
              className="rounded border border-white/20 px-2 py-1 text-xs text-white/70 hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              Reset defaults
            </button>
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {ACTIVE_SPEAKER_TUNING_FIELDS.map((field) => {
              const value = activeSpeakerTuning[field.key];

              return (
                <label key={field.key} className="block rounded border border-white/10 bg-white/[0.03] px-3 py-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs text-white/80">{field.label}</div>
                      <div className="mt-0.5 text-[10px] text-white/45">{field.description}</div>
                    </div>
                    <div className="font-mono text-[11px] text-white/65">{formatTuningValue(field, value)}</div>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="range"
                      min={field.min}
                      max={field.max}
                      step={field.step}
                      value={value}
                      disabled={!canTuneActiveSpeaker}
                      onChange={(event) => handleTuningChange(field.key, Number(event.target.value))}
                      className="h-1.5 min-w-0 flex-1 cursor-pointer accent-white disabled:cursor-not-allowed disabled:opacity-45"
                    />
                    <input
                      type="number"
                      min={field.min}
                      max={field.max}
                      step={field.step}
                      value={value}
                      disabled={!canTuneActiveSpeaker}
                      onChange={(event) => handleTuningChange(field.key, Number(event.target.value))}
                      className="w-20 rounded border border-white/15 bg-black/30 px-2 py-1 text-right font-mono text-[11px] text-white outline-none focus:border-white/45 disabled:cursor-not-allowed disabled:opacity-45"
                    />
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          <div className="border-b border-white/12 px-4 py-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-sm leading-tight">Camera routing</h3>
                <p className="mt-1 text-[11px] text-white/55">
                  Active profile {getShortId(cameraSummary?.activeModelProfileId)} · representative {getShortId(cameraSummary?.representativeModelParticipantId)} · switcher cameras {cameraSummary?.switcherCameraCount ?? 0}
                </p>
                <p className="mt-1 text-[11px] text-white/45">
                  Local preferred device {getOptionalShortId(cameraSummary?.preferredCameraDeviceId)}
                </p>
              </div>
              <div className="text-right font-mono text-[11px] text-white/55">
                active ids {(cameraSummary?.activeModelParticipantIds.length ?? 0) || 0}
              </div>
            </div>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full border-collapse text-left text-[11px]">
                <thead className="text-[9px] uppercase tracking-[0.12em] text-white/40">
                  <tr>
                    <th className="py-1 pr-3 font-medium">Participant</th>
                    <th className="py-1 pr-3 font-medium">Role</th>
                    <th className="py-1 pr-3 font-medium">Profile</th>
                    <th className="py-1 pr-3 font-medium">Active</th>
                    <th className="py-1 pr-3 font-medium">Label</th>
                    <th className="py-1 pr-3 font-medium">Camera</th>
                    <th className="py-1 pr-3 font-medium">Device</th>
                    <th className="py-1 pr-3 font-medium">Track</th>
                  </tr>
                </thead>
                <tbody>
                  {(cameraSummary?.rows ?? []).map((row) => (
                    <tr key={row.participantId} className="border-t border-white/8">
                      <td className="max-w-[150px] truncate py-1.5 pr-3">
                        {row.participantName}{row.isLocal ? ' (you)' : ''}
                      </td>
                      <td className="py-1.5 pr-3">{row.sessionRole}</td>
                      <td className="py-1.5 pr-3 font-mono">{getShortId(row.profileId)}</td>
                      <td className={`py-1.5 pr-3 ${row.isActiveModelCamera ? 'text-[#b8f0c2]' : row.isActiveModelProfile ? 'text-white' : 'text-white/45'}`}>
                        {row.isActiveModelCamera ? 'camera' : row.isActiveModelProfile ? 'profile' : 'no'}
                      </td>
                      <td className="py-1.5 pr-3 font-mono">{row.cameraLabel ?? '...'}</td>
                      <td className="py-1.5 pr-3">
                        {row.hasCameraPublication
                          ? `${row.cameraMuted ? 'muted' : 'on'}${row.mediaWidth && row.mediaHeight ? ` · ${formatSize(row.mediaWidth, row.mediaHeight)}` : ''}`
                          : 'none'}
                      </td>
                      <td
                        className={`max-w-[130px] truncate py-1.5 pr-3 font-mono ${row.mediaDeviceMatchesPreferred ? 'text-[#b8f0c2]' : ''}`}
                        title={row.mediaDeviceId ?? row.mediaLabel ?? undefined}
                      >
                        {getOptionalShortId(row.mediaDeviceId)}
                        {row.mediaDeviceMatchesPreferred ? ' *' : ''}
                      </td>
                      <td className="max-w-[130px] truncate py-1.5 pr-3 font-mono" title={row.mediaLabel ?? row.trackSid ?? undefined}>
                        {row.trackSid ? getShortId(row.trackSid) : row.trackName || row.mediaLabel || '...'}
                      </td>
                    </tr>
                  ))}
                  {cameraSummary && cameraSummary.rows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="py-4 text-center text-white/45">
                        No participants in session state yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <table className="w-full border-collapse text-left text-xs">
            <thead className="sticky top-0 bg-[#111] text-[10px] uppercase tracking-[0.12em] text-white/45">
              <tr>
                <th className="px-3 py-2 font-medium">Participant</th>
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium">Dir</th>
                <th className="px-3 py-2 font-medium">Bitrate</th>
                <th className="px-3 py-2 font-medium">Frame</th>
                <th className="px-3 py-2 font-medium">FPS</th>
                <th className="px-3 py-2 font-medium">Quality</th>
              </tr>
            </thead>
            <tbody>
              {(snapshot?.rows ?? []).map((row) => (
                <tr key={`${row.participantId}:${row.direction}:${row.trackSid}`} className="border-t border-white/8">
                  <td className="max-w-[180px] truncate px-3 py-2">{row.participantName}</td>
                  <td className="px-3 py-2">{row.role}</td>
                  <td className="px-3 py-2">{row.direction}</td>
                  <td className={`px-3 py-2 font-mono ${row.status === 'warn' ? 'text-[#ffb199]' : ''}`}>
                    {formatBitrate(row.bitrateMbps)}
                  </td>
                  <td className={`px-3 py-2 font-mono ${row.status === 'warn' ? 'text-[#ffb199]' : ''}`}>
                    {formatSize(row.width, row.height)}
                  </td>
                  <td className="px-3 py-2 font-mono">
                    {row.framesPerSecond === null ? '...' : Math.round(row.framesPerSecond)}
                  </td>
                  <td className="px-3 py-2">
                    {row.videoQuality ?? (row.subscribed === false ? 'not subscribed' : '...')}
                  </td>
                </tr>
              ))}
              {snapshot && snapshot.rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-white/50">
                    No camera tracks published yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {hasWarnings && (
          <div className="border-t border-white/12 bg-[#1b100d] px-4 py-3 text-xs text-[#ffccb8]">
            {snapshot?.warnings[0]}
          </div>
        )}
      </div>
    </div>
  );
}
